// src/components/pages/ChallengeCreator/ChallengeCreatorPage.js

import React, { useEffect, useState } from "react";
import { getAuth } from "firebase/auth";
import { useParams } from "react-router-dom";
import {
  Container,
  Box,
  Typography,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import TitleDescriptionSection from "../../molecules/ChallengeCreator/TitleDescriptionSection";
import ChallengeRulesSection from "../../molecules/ChallengeCreator/ChallengeRulesSection";
import ChallengeDatesAndPrizingSection from "../../molecules/ChallengeCreator/ChallengeDatesAndPrizingSection";
import ChallengeCoverForm from "../../organisms/ChallengeCreator/ChallengeCoverForm";
import { useTheme } from "styled-components";
import useChallengeData from "../../../hooks/useChallengeData";
import useChallengeForm from "../../../hooks/useChallengeForm";

const ChallengeCreatorPage = () => {
  const { challengeId } = useParams();
  const theme = useTheme();
  const auth = getAuth();
  const user = auth.currentUser;
  const userId = user ? user.uid : null;

  // Fetch challenge data using the custom hook
  const {
    creatorInfo: initialCreatorInfo,
    challenge,
    loading,
    isOwner,
    hasSubmissions,
    error,
  } = useChallengeData(challengeId, userId);

  // State for creatorInfo in case it's not set by useChallengeData
  const [creatorInfo, setCreatorInfo] = useState(initialCreatorInfo);

  useEffect(() => {
    // Check if creatorInfo is null and fetch from localStorage if necessary
    if (!creatorInfo) {
      const storedProfile = localStorage.getItem("userProfile");
      if (storedProfile) {
        try {
          const profileData = JSON.parse(storedProfile);
          const newCreatorInfo = {
            creatorUid: profileData.uid,
            displayName: profileData.displayName,
            profileImage: profileData.profileImage,
            userName: profileData.userName || profileData.username, // userName may be in different fields
          };
          setCreatorInfo(newCreatorInfo); // Set the creatorInfo
        } catch (error) {
          console.error("Error parsing user profile from localStorage:", error);
        }
      }
    }
  }, [creatorInfo]);

  // Initialize form state using the custom hook
  const {
    formData,
    rules,
    currentRule,
    errors,
    showSnackbar,
    snackbarMessage,
    published,
    isDraftSaved,
    handleChange,
    handleToggleChange,
    handleCrowdFundingToggleChange,
    handleAddRule,
    handleDeleteRule,
    handleImageUpload,
    handleSubmit,
    handleSaveAsDraft,
    setShowSnackbar,
    setCurrentRule,
  } = useChallengeForm(challengeId, challenge, creatorInfo, hasSubmissions);

  // Handle loading state
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Handle errors
  if (error) {
    return (
      <Container maxWidth="md">
        <Box sx={{ my: 4, textAlign: "center" }}>
          <Typography
            variant="h6"
            color="error"
            sx={{
              fontFamily: "basic-sans",
            }}
          >
            {error}
          </Typography>
        </Box>
      </Container>
    );
  }

  // If editing, ensure the user is the owner
  if (challengeId && !isOwner) {
    return (
      <Container maxWidth="md">
        <Box sx={{ my: 4, textAlign: "center" }}>
          <Typography
            variant="h6"
            color="error"
            sx={{
              fontFamily: "basic-sans",
            }}
          >
            You do not have permission to edit this challenge.
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ my: 4 }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: "bold",
            fontFamily: "basic-sans",
            textAlign: "center",
            mb: 2,
          }}
        >
          {challengeId
            ? published
              ? "Edit Your Live Challenge"
              : "Edit Your Draft Challenge"
            : "Create a New Challenge"}
        </Typography>

        {hasSubmissions && (
          <Typography
            variant="body2"
            sx={{
              color: theme.colors.red,
              fontFamily: "basic-sans",
              textAlign: "center",
              mb: 4,
            }}
          >
            You can't edit this challenge because it already has submissions.
          </Typography>
        )}

        <TitleDescriptionSection
          formData={formData}
          handleChange={handleChange}
          errors={errors}
          disabled={hasSubmissions}
        />

        <ChallengeDatesAndPrizingSection
          formData={formData}
          handleChange={handleChange}
          handleToggleChange={handleToggleChange}
          handleCrowdFundingToggleChange={handleCrowdFundingToggleChange}
          theme={theme}
          errors={errors}
          disabled={hasSubmissions}
        />

        <ChallengeRulesSection
          rules={rules}
          currentRule={currentRule}
          setCurrentRule={setCurrentRule}
          handleAddRule={handleAddRule}
          handleDeleteRule={handleDeleteRule}
          theme={theme}
          disabled={hasSubmissions}
        />

        <ChallengeCoverForm
          onImageUpload={handleImageUpload}
          formData={formData}
          setFormData={handleChange}
          userId={userId}
          userData={creatorInfo}
          disabled={hasSubmissions}
        />

        <Box textAlign="center" mt={4}>
          {challengeId ? (
            <>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveAsDraft}
                disabled={loading || hasSubmissions}
                sx={{
                  backgroundColor: theme.colors.mediumPurple,
                  padding: "10px 20px",
                  fontFamily: "basic-sans",
                  fontWeight: "bold",
                  textTransform: "none",
                  borderRadius: "50px",
                  "&:hover": { backgroundColor: theme.colors.blue },
                }}
              >
                {loading ? (
                  <CircularProgress size={24} sx={{ color: "white" }} />
                ) : isDraftSaved ? (
                  "Save Changes"
                ) : (
                  "Save Draft"
                )}
              </Button>
              {!published && ( // Changed from !challenge.published to !published
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleSubmit}
                  disabled={loading || hasSubmissions}
                  sx={{
                    ml: 2,
                    padding: "10px 20px",
                    fontFamily: "basic-sans",
                    fontWeight: "bold",
                    textTransform: "none",
                    borderRadius: "50px",
                    backgroundColor: theme.colors.green,
                    "&:hover": {
                      backgroundColor: theme.colors.blue,
                      color: "white",
                    },
                  }}
                >
                  Post Your Challenge
                </Button>
              )}
            </>
          ) : (
            <>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={loading}
                sx={{
                  backgroundColor: theme.colors.green,
                  padding: "10px 20px",
                  fontFamily: "basic-sans",
                  fontWeight: "bold",
                  textTransform: "none",
                  borderRadius: "50px",
                  "&:hover": { backgroundColor: theme.colors.blue },
                }}
              >
                {loading ? (
                  <CircularProgress size={24} sx={{ color: "white" }} />
                ) : (
                  "Post Your Challenge"
                )}
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleSaveAsDraft}
                disabled={loading}
                sx={{
                  ml: 2,
                  padding: "10px 20px",
                  fontFamily: "basic-sans",
                  fontWeight: "bold",
                  textTransform: "none",
                  borderRadius: "50px",
                  backgroundColor: theme.colors.mediumPurple,
                  "&:hover": {
                    backgroundColor: theme.colors.blue,
                    color: "white",
                  },
                }}
              >
                {isDraftSaved ? "Save Changes" : "Save Draft"}
              </Button>
            </>
          )}
        </Box>
      </Box>

      <Snackbar
        open={showSnackbar}
        autoHideDuration={4000}
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowSnackbar(false)}
          severity={
            snackbarMessage.includes("successfully") ? "success" : "error"
          }
          sx={{
            backgroundColor: snackbarMessage.includes("successfully")
              ? theme.colors.green
              : theme.colors.red,
            color: "white",
            fontFamily: "basic-sans",
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ChallengeCreatorPage;
